/**
 * Invocacoes — a listagem crua de invocações, paginada, com o TOTAL do filtro sempre à
 * vista. Filtros: agente, etapa, tipo de rodada, status e canal (o período e o projeto
 * vêm da barra da tela). Agente e etapa têm seletor com os valores do período
 * (GET /observability/filters): escolher um valor filtra pelo nome exato; digitar um
 * trecho filtra por "contém". Cada linha leva à execução e ao agente.
 */
import { useEffect, useState } from 'react';
import { C } from '@/theme/palette';
import { LoadingSpinner, QueryError } from '@/components/ui/QueryState';
import { dataHora } from '@/lib/format';
import { MAX_POR_PAGINA, filtroExatoOuContem, useObsFiltros, useObsInvocacoes } from '@/api/observability';
import { CANAIS, STATUS, TIPOS_DE_RODADA, duracao, idCurto, inteiro, rotulo } from './formato';
import {
  AvisoSemCusto, Custo, Nota, Paginacao, Selo, Tokens, Vazio, corDoStatus, estilos,
} from './partes';

const TAMANHOS = [50, 100, 200, MAX_POR_PAGINA];
const VAZIOS = { agente: '', etapa: '', tipo: '', status: '', canal: '' };

export function Invocacoes({
  periodo, projectId, agenteInicial = '', nomeDoProjeto = (id) => id, aoAbrirExecucao, aoAbrirAgente,
}) {
  const s = estilos();
  const [rascunho, setRascunho] = useState({ ...VAZIOS, agente: agenteInicial });
  const [aplicado, setAplicado] = useState({ ...VAZIOS, agente: agenteInicial });
  const [limit, setLimit] = useState(50);
  const [offset, setOffset] = useState(0);

  // Chegou de outro lugar pedindo um agente (ex.: "Ver invocações deste agente").
  useEffect(() => {
    setRascunho((r) => ({ ...r, agente: agenteInicial }));
    setAplicado((a) => ({ ...a, agente: agenteInicial }));
    setOffset(0);
  }, [agenteInicial]);
  // Mudou o período ou o projeto: volta à primeira página.
  useEffect(() => { setOffset(0); }, [periodo?.de, periodo?.ate, projectId]);

  const valores = useObsFiltros({ de: periodo?.de, ate: periodo?.ate, projectId }).data;
  const agentes = valores?.agentes || [];
  const etapas = valores?.etapas || [];
  // Valor da lista (ou o agente que veio de outra aba) → nome exato; outro texto → "contém".
  const nomesDeAgente = [...agentes.map((a) => a.valor), ...(agenteInicial ? [agenteInicial] : [])];
  const filtros = {
    ...filtroExatoOuContem(aplicado.agente, nomesDeAgente, { exato: 'agent_name', contem: 'agente_contem' }),
    ...filtroExatoOuContem(aplicado.etapa, etapas.map((e) => e.valor), { exato: 'etapa', contem: 'etapa_contem' }),
    project_id: projectId, tipo_de_rodada: aplicado.tipo, status: aplicado.status, canal: aplicado.canal,
    de: periodo?.de, ate: periodo?.ate,
  };
  const { data, isLoading, isError, error, refetch, isFetching } = useObsInvocacoes(filtros, { limit, offset });
  const visiveis = data?.custos_visiveis !== false;
  const itens = data?.itens || [];

  const campo = (k) => (e) => setRascunho((r) => ({ ...r, [k]: e.target.value }));
  const aplicar = (ev) => { ev.preventDefault(); setAplicado(rascunho); setOffset(0); };
  const limpar = () => { setRascunho(VAZIOS); setAplicado(VAZIOS); setOffset(0); };
  const opcoes = (lista) => lista.map((v) => <option key={v} value={v}>{rotulo(v)}</option>);

  return (
    <div data-testid="obs-invocacoes">
      <form onSubmit={aplicar} style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end', marginBottom: 6 }}>
        <label style={s.rotuloDeCampo}>
          Agente
          <input aria-label="Filtrar por agente" list="obs-agentes" value={rascunho.agente} onChange={campo('agente')}
            placeholder="escolha ou digite um trecho" style={{ ...s.campo, minWidth: 220 }} />
          <datalist id="obs-agentes" data-testid="opcoes-de-agente">
            {agentes.map((a) => <option key={a.valor} value={a.valor} label={`${inteiro(a.invocacoes)} invocação(ões)`} />)}
          </datalist>
        </label>
        <label style={s.rotuloDeCampo}>
          Etapa
          <input aria-label="Filtrar por etapa" list="obs-etapas" value={rascunho.etapa} onChange={campo('etapa')}
            placeholder="escolha ou digite um trecho" style={{ ...s.campo, minWidth: 200 }} />
          <datalist id="obs-etapas" data-testid="opcoes-de-etapa">
            {etapas.map((e) => <option key={e.valor} value={e.valor} label={`${inteiro(e.invocacoes)} invocação(ões)`} />)}
          </datalist>
        </label>
        <label style={s.rotuloDeCampo}>
          Tipo de rodada
          <select aria-label="Filtrar por tipo de rodada" value={rascunho.tipo} onChange={campo('tipo')} style={s.campo}>
            <option value="">Todos</option>{opcoes(TIPOS_DE_RODADA)}
          </select>
        </label>
        <label style={s.rotuloDeCampo}>
          Status
          <select aria-label="Filtrar por status" value={rascunho.status} onChange={campo('status')} style={s.campo}>
            <option value="">Todos</option>{opcoes(STATUS)}
          </select>
        </label>
        <label style={s.rotuloDeCampo}>
          Canal
          <select aria-label="Filtrar por canal" value={rascunho.canal} onChange={campo('canal')} style={s.campo}>
            <option value="">Todos</option>{opcoes(CANAIS)}
          </select>
        </label>
        <label style={s.rotuloDeCampo}>
          Por página
          <select aria-label="Itens por página" value={limit} style={s.campo}
            onChange={(e) => { setLimit(Number(e.target.value)); setOffset(0); }}>
            {TAMANHOS.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
        <button type="submit" style={s.botao}>Aplicar filtros</button>
        <button type="button" style={{ ...s.botao, color: C.neutralDim }} onClick={limpar}>Limpar</button>
      </form>
      <Nota>
        Agente e etapa: escolha um valor da lista (nome exato) ou digite um trecho (contém, sem
        diferenciar maiúsculas). Esta lista inclui invocações de execuções excluídas (elas ficam
        fora das médias das outras abas).
        {valores?.sem_etapa > 0 ? ` ${inteiro(valores.sem_etapa)} invocação(ões) do período não têm etapa.` : ''}
      </Nota>

      {isLoading ? <LoadingSpinner label="Carregando invocações..." />
        : isError ? <div style={{ marginTop: 14 }}><QueryError error={error} onRetry={refetch} /></div>
          : (
            <>
              {!visiveis ? <div style={{ marginTop: 12 }}><AvisoSemCusto /></div> : null}
              <Paginacao total={data?.total || 0} limit={limit} offset={data?.offset ?? offset}
                retornados={data?.retornados ?? itens.length} aoMudar={setOffset} carregando={isFetching} />
              {itens.length === 0 ? (
                <Vazio>Nenhuma invocação encontrada com estes filtros no período.</Vazio>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={s.tabela}>
                    <thead>
                      <tr>
                        <th style={s.th}>Quando</th>
                        <th style={s.th}>Agente</th>
                        <th style={s.th}>Projeto</th>
                        <th style={s.th}>Etapa · passo</th>
                        <th style={s.th}>Rodada</th>
                        <th style={s.thNum}>Custo</th>
                        <th style={s.thNum}>Tokens</th>
                        <th style={s.thNum}>Duração</th>
                        <th style={s.th}>Status</th>
                        <th style={s.th}>Execução</th>
                      </tr>
                    </thead>
                    <tbody>
                      {itens.map((i, n) => (
                        <tr key={i.id || n} data-testid="linha-invocacao">
                          <td style={{ ...s.td, whiteSpace: 'nowrap', color: C.neutralDim }}>{dataHora(i.occurred_at)}</td>
                          <td style={{ ...s.td, wordBreak: 'break-word', minWidth: 150 }}>
                            <button type="button" style={s.link} onClick={() => aoAbrirAgente(i.agent_name)}>{i.agent_name}</button>
                            <div style={{ color: C.neutralMuted, fontSize: 12 }}>
                              {rotulo(i.canal)}{i.tier ? ` · ${i.tier}` : ''}
                            </div>
                          </td>
                          <td style={{ ...s.td, wordBreak: 'break-word' }} title={i.project_id || ''}>
                            {i.project_id ? nomeDoProjeto(i.project_id) : '—'}
                          </td>
                          <td style={{ ...s.td, wordBreak: 'break-word' }}>
                            {i.etapa || '—'}
                            <div style={{ color: C.neutralMuted, fontSize: 12 }}>{i.passo}</div>
                          </td>
                          <td style={{ ...s.td, whiteSpace: 'nowrap', color: i.tipo_de_rodada === 'principal' ? C.text : C.warn }}>
                            {rotulo(i.tipo_de_rodada)} · {inteiro(i.rodada)}
                          </td>
                          <td style={s.tdNum}><Custo valor={i.cost_usd} visivel={visiveis} /></td>
                          <td style={s.tdNum}><Tokens item={i} /></td>
                          <td style={s.tdNum}>{duracao(i.duracao_ms)}</td>
                          <td style={{ ...s.td, maxWidth: 300, overflowWrap: 'anywhere' }}>
                            <Selo cor={corDoStatus(i.status)}>{rotulo(i.status)}</Selo>
                            {i.erro_tipo || i.erro_resumo ? (
                              <div style={{ color: C.bad, fontSize: 12.5, marginTop: 4 }}>
                                {i.erro_tipo ? <strong>{i.erro_tipo}</strong> : null}
                                {i.erro_tipo && i.erro_resumo ? ': ' : ''}{i.erro_resumo}
                              </div>
                            ) : null}
                          </td>
                          <td style={{ ...s.td, whiteSpace: 'nowrap' }}>
                            {i.execution_id ? (
                              <button type="button" style={s.link} title={i.execution_id}
                                aria-label={`Abrir a execução ${i.execution_id}`}
                                onClick={() => aoAbrirExecucao(i.execution_id)}>
                                {idCurto(i.execution_id)}
                              </button>
                            ) : <span style={{ color: C.neutralMuted }}>fora de execução</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {itens.length > 0 ? (
                <Paginacao total={data?.total || 0} limit={limit} offset={data?.offset ?? offset}
                  retornados={data?.retornados ?? itens.length} aoMudar={setOffset} carregando={isFetching} />
              ) : null}
            </>
          )}
    </div>
  );
}

export default Invocacoes;
